import React, { useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

function removeSplash() {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const splash = document.getElementById('splash-overlay');
      if (splash) {
        splash.style.opacity = '0';
        splash.style.transition = 'opacity 0.15s ease-out';
        setTimeout(() => splash.remove(), 150);
      }
    });
  });
}

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'user' | 'admin';
  /**
   * Pflicht-Abo erforderlich? Default: true.
   * Setze auf false für Routen, die ein Nutzer auch OHNE aktives Pflicht-Abo
   * erreichen muss (z.B. /abo-erforderlich selbst, /checkout, /profil, /feedback).
   * Admins umgehen die Prüfung immer.
   */
  requireSubscription?: boolean;
  redirectTo?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole = 'user',
  requireSubscription = true,
  redirectTo = '/login'
}) => {
  const { isAuthenticated, user, isLoading, hasRequiredSubscription, isLoadingSubscriptions, subscriptionsInitialized } = useAuth();
  const location = useLocation();
  const splashRemoved = useRef(false);

  // Splash als "managed" markieren, damit App.tsx es nicht vorzeitig entfernt
  useEffect(() => {
    const splash = document.getElementById('splash-overlay');
    if (splash) splash.dataset.managed = 'true';
  }, []);

  // Splash entfernen, sobald Auth fertig ist (doppeltes rAF wartet auf Layout-Paint)
  useEffect(() => {
    if (!isLoading && !splashRemoved.current) {
      splashRemoved.current = true;
      removeSplash();
    }
  }, [isLoading]);

  // Während Auth-Check: nichts rendern (Splash-Overlay verdeckt alles)
  if (isLoading) {
    return null;
  }

  // Redirect if not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate
      to={`${redirectTo}?redirect=${encodeURIComponent(location.pathname + location.search)}`}
      state={{ from: location }}
      replace
    />;
  }

  // Check role requirements
  if (requiredRole === 'admin' && user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4 text-white">Zugriff verweigert</h1>
          <p className="mb-6 text-gray-400">Sie haben keine Berechtigung für diese Seite.</p>
          <a
            href="/dashboard"
            className="inline-block px-6 py-3 rounded-md transition-colors font-medium bg-brand text-gray-950 hover:bg-brand-hover"
          >
            Zum Dashboard
          </a>
        </div>
      </div>
    );
  }

  // Pflicht-Abo-Gate: User muss mindestens ein zahlpflichtiges Abo aus einer Pflicht-Family haben.
  // Admins haben hier bereits hasRequiredSubscription=true via Backend.
  //
  // Race-Condition-Schutz: Beim Page-Refresh kommt erst isAuthenticated=true,
  // danach erst der refreshSubscriptions-Call. Solange der erste Call NICHT
  // durch ist (subscriptionsInitialized=false) oder gerade laeuft (isLoading-
  // Subscriptions=true), darf KEIN Redirect ausgeloest werden -- sonst landet
  // der eingeloggte Abonnent faelschlich auf /abo-erforderlich.
  if (requireSubscription && user.role !== 'admin') {
    if (!subscriptionsInitialized || isLoadingSubscriptions) {
      return null;
    }
    if (!hasRequiredSubscription) {
      return <Navigate to="/pakete" replace state={{ from: location }} />;
    }
  }

  return <>{children}</>;
};